import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Loader2, Heart, LogOut, Package } from "lucide-react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { useDocumentSeo } from "../lib/seo";

export default function MyAccount() {
    useDocumentSeo({ title: "My Account — TT Admins", noindex: true });
    const { user, loading, logout } = useAuth();

    if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
    if (!user) return <Navigate to="/" replace />;

    return (
        <div data-testid="my-account-page">
            <section className="dawn-wash">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between">
                        <div className="flex items-center gap-5">
                            {user.picture ? (
                                <img src={user.picture} alt="" className="w-16 h-16 rounded-full ring-2 ring-[var(--brand-gold)]/50" />
                            ) : (
                                <div className="w-16 h-16 rounded-full bg-[var(--brand-navy)] text-white flex items-center justify-center font-serif text-2xl">
                                    {user.name?.charAt(0).toUpperCase()}
                                </div>
                            )}
                            <div>
                                <div className="eyebrow">Welcome back</div>
                                <h1 className="font-serif text-3xl sm:text-4xl text-[var(--brand-navy)]">{user.name}</h1>
                                <div className="text-sm text-slate-500">{user.email}</div>
                            </div>
                        </div>
                        <button onClick={logout} className="btn-outline-navy !px-5 !py-2 text-sm" data-testid="account-logout-btn">
                            <LogOut className="w-4 h-4 mr-2" /> Sign out
                        </button>
                    </div>

                    {user.role === "admin" && (
                        <Link to="/admin" className="mt-6 inline-flex items-center text-sm font-semibold text-[var(--brand-coral)] hover:underline" data-testid="account-admin-link">
                            → Go to Admin Console
                        </Link>
                    )}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="flex items-center gap-2 mb-6 border-b border-slate-200 pb-3" data-testid="account-tabs">
                    <Heart className="w-4 h-4 text-[var(--brand-coral)]" />
                    <span className="font-serif text-xl text-[var(--brand-navy)]">My Donations</span>
                </div>

                <DonationHistory />
            </section>
        </div>
    );
}

function DonationHistory() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/me/donations").then((r) => setData(r.data)); }, []);
    if (!data) return <Loader2 className="animate-spin" />;
    const list = data.donations || [];

    if (list.length === 0) {
        return (
            <div className="faith-card p-12 text-center" data-testid="my-donations-list-empty">
                <Package className="w-10 h-10 text-slate-300 mx-auto mb-4" />
                <div className="font-serif text-xl text-[var(--brand-navy)] mb-2">
                    No donations yet.
                </div>
                <div className="text-sm text-slate-500 mb-6">
                    When you make a donation, your receipts will appear here.
                </div>
                <Link to="/workforce" className="btn-gold">
                    Support the Mission
                </Link>
            </div>
        );
    }

    return (
        <div data-testid="my-donations-list">
            <div className="grid grid-cols-2 gap-4 mb-5">
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total</div>
                    <div className="text-3xl font-serif text-[var(--brand-navy)] mt-2">${data.total.toFixed(2)}</div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Count</div>
                    <div className="text-3xl font-serif text-[var(--brand-navy)] mt-2">{data.count}</div>
                </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider text-left">
                        <tr>
                            <th className="px-5 py-3">Date</th>
                            <th className="px-5 py-3">Gift</th>
                            <th className="px-5 py-3">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((r, i) => (
                            <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/50">
                                <td className="px-5 py-3 text-slate-600 text-xs whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                                <td className="px-5 py-3 text-slate-700">
                                    {r.package_label || "Custom Gift"}
                                    {r.provider === "paypal" && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#003087]/10 text-[#003087] font-semibold">PayPal</span>}
                                </td>
                                <td className="px-5 py-3 font-semibold text-[var(--brand-navy)]">${r.amount.toFixed(2)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
